import React, { useState, useEffect, useCallback, useRef } from 'react'
import { getCopyByGradeband } from '../../../../../services/ThemingService'
import { COPY_STRINGS } from '../../../../../constants/translatedCopy'
import StudentInstruction from '../../../Components/StudentInstruction'
import { logSessionInfo } from '../../../../../services/logger'
import LandingView from './LandingView'
import ListenToRecordingView from './ListenToRecordingView'
import CheckHeadsetView from './CheckHeadsetView'
import TestMicrophoneView from './TestMicrophoneView'
import ASRTestView from './ASRTestView'
import AdditionalDetailsView from './AdditionalDetailsView'
import RetryView from './RetryView'
import TeacherRetryView from './TeacherRetryView'
import ErrorView from './ErrorView'
import LocalRecordingView from './LocalRecordingView'
import LocalPlaybackView from './LocalPlaybackView'
import RetestMicrophoneView from './RetestMicrophoneView'
import StudentUseIssueView from './StudentUseIssueView'
import AudioIssueInterstitialView from './AudioIssueInterstitialView'
import RepeatAudioIssueInterstitialView from './RepeatAudioIssueInterstitialView'
import Heading from './Heading'
import './ASRTeacherModal.scss'

const VIEWS = {
  RETRY: 'retry',
  LANDING: 'landing',
  LISTEN_TO_RECORDING: 'listen_to_recording',
  CHECK_HEADSET: 'check_headset',
  TEST_MICROPHONE: 'test_microphone',
  RETEST_MICROPHONE: 'retest_microphone',
  RUNNING_ASR_TEST: 'running_asr_test',
  ADDITIONAL_DETAILS: 'additional_details',
  RETRY_AFTER_FIRST_FAILURE: 'retry_after_first_failure',
  ERROR: 'error',
  LOCAL_RECORDING: 'local_recording',
  LOCAL_PLAYBACK: 'local_playback',
  STUDENT_USE_ISSUE: 'student_use_issue',
  AUDIO_ISSUE_INTERSTITIAL: 'audio_issue_interstitial',
  REPEAT_AUDIO_ISSUE_INTERSTITIAL: 'repeat_audio_issue_interstitial',
}

export default function ASRTeacherModal(props) {
  const { teacherASRAttempts, incrementTeacherASRAttempts, resetTeacherASRAttempts, midSessionMicTroubleshoot, audioIssueType = {}, midSessionTroubleshootSeen, onRestartSession, onRestartSessionSuppressingTroubleshoot } = props;
  // Check if we're returning from a teacher test
  const teacherTestResult = new URLSearchParams(window.location.search).get('teacherTestResult')
  const [teacherTestAttempts, setTeacherTestAttempts] = useState(teacherASRAttempts);
  
  // Set initial view based on whether we have a teacher test result
  const getInitialView = () => {
    if (teacherTestResult === 'timeout' || teacherTestResult === 'failed') {
      // First failure - go to teacher retry view
      if (teacherTestAttempts <= 1) return VIEWS.RETRY_AFTER_FIRST_FAILURE;
      return VIEWS.ERROR;
    } else if (teacherTestResult === 'backgroundNoise') {
      return VIEWS.LANDING;
    } else if (teacherTestResult === 'success') {
      return VIEWS.RETRY;
    }
    return VIEWS.RETRY;
  }
  
  const [currentView, setCurrentView] = useState(getInitialView())
  const [hasVisistedListenToRecording, setHasVisistedListenToRecording] = useState(false)
  const [isFirstRetryVisit, setIsFirstRetryVisit] = useState(true)
  const [landingPatter, setLandingPatter] = useState(null)
  const [localAudioBlobUrl, setLocalAudioBlobUrl] = useState(null)
  const studentUseIssueBackNav = useRef(null)
  const checkHeadsetBackNav = useRef(null)
  
  // Ref to track blob URL for cleanup on unmount (avoids stale closure in useEffect cleanup)
  const localAudioBlobUrlRef = useRef(null)

  const { speechProps } = props

  const asrCopy = getCopyByGradeband(COPY_STRINGS, 'student_checks')
  const [retryPatter, setRetryPatter] = useState(asrCopy.handle_get_teacher)

  // Keep ref in sync with state for cleanup on unmount
  useEffect(() => {
    localAudioBlobUrlRef.current = localAudioBlobUrl;
  }, [localAudioBlobUrl]);

  useEffect(() => {
    // Log that teacher was called (when modal opens)
    logSessionInfo('teacherTroubleshoot_CALLED_TEACHER', {})
    
    return () => {
      speechProps.abortSpeech()
      // Cleanup local audio blob URL on unmount using ref to get current value
      if (localAudioBlobUrlRef.current) {
        URL.revokeObjectURL(localAudioBlobUrlRef.current);
      }
    }
  }, [])

  useEffect(() => {
    if (currentView === VIEWS.LISTEN_TO_RECORDING) {
      setHasVisistedListenToRecording(true)
    }
  }, [currentView])
  
  // Handle teacher test result when returning from ASR test
  useEffect(() => {
    if (teacherTestResult) {
      // Clear the URL parameter
      // const url = new URL(window.location)
      // url.searchParams.delete('teacherTestResult')
      // window.history.replaceState({}, '', url)
      
      // Handle the result
      if (teacherTestResult === 'success') {
        // Test was successful - speak success message and go to retry after success
        speechProps.speakWithPromise({ textToSpeak: asrCopy.teacher_modal_retry_return_1 });
        goToRetry();
      } else if (teacherTestResult === 'backgroundNoise') {
        speechProps.speakWithPromise({ textToSpeak: asrCopy.teacher_mode_asr_failure_high_bgn_one })
        goToLanding()
      } else if (teacherTestResult === 'timeout' || teacherTestResult === 'failed') {
        // First failure - go to teacher retry view
        if (teacherTestAttempts <= 1) {
          goToTeacherRetry();
        } else {
          // Second failure - go to error view
          goToError();
        }
      }
    }
  }, [teacherTestResult]);
  
  const updateCurrentView = (view) => {
    setCurrentView(view)
  }

  const goToRetry = () => {
    // Set to false because this is only called from views subsequent to the first retry view
    setIsFirstRetryVisit(false)
    // Clear landing patter when going back to retry
    setLandingPatter(null)
    updateCurrentView(VIEWS.RETRY)
  } 

  const goToLanding = () => {
    updateCurrentView(VIEWS.LANDING)
  }

  const goToListenToRecording = () => {
    updateCurrentView(VIEWS.LISTEN_TO_RECORDING)
  }

  const goToCheckHeadset = (backNavCallback = null) => {
    checkHeadsetBackNav.current = backNavCallback || null
    updateCurrentView(VIEWS.CHECK_HEADSET)
  }

  const goToTestMicrophone = () => {
    setTeacherTestAttempts(0); // Reset attempt count - local state update - can be used before component unmounts
    resetTeacherASRAttempts(); // Reset attempt count - global state update - can be used after component unmounts and re-mounts
    updateCurrentView(VIEWS.TEST_MICROPHONE)
  }

  const goToASRTest = () => {
    setTeacherTestAttempts(teacherTestAttempts + 1); // Increment attempt count - local state update - can be used before component unmounts
    incrementTeacherASRAttempts(); // Increment attempt count - global state update - can be used after component unmounts and re-mounts
    updateCurrentView(VIEWS.RUNNING_ASR_TEST)
  }

  // const goToAdditionalDetails = () => {
  //   updateCurrentView(VIEWS.ADDITIONAL_DETAILS)
  // }

  const goToError = () => {
    updateCurrentView(VIEWS.ERROR)
  }

  const goToTeacherRetry = () => {
    updateCurrentView(VIEWS.RETRY_AFTER_FIRST_FAILURE)
  }

  const goToStudentUseIssue = (backNavCallback = null) => {
    studentUseIssueBackNav.current = backNavCallback || null
    updateCurrentView(VIEWS.STUDENT_USE_ISSUE)
  }

  const goToAudioIssueInterstitial = () => {
    updateCurrentView(VIEWS.AUDIO_ISSUE_INTERSTITIAL)
  }

  /**
   * Cleans up the local audio blob URL by revoking it.
   * Should be called when navigating away from playback or before creating a new recording.
   */
  const cleanupLocalAudioBlobUrl = useCallback(() => {
    if (localAudioBlobUrl) {
      URL.revokeObjectURL(localAudioBlobUrl);
      setLocalAudioBlobUrl(null);
    }
  }, [localAudioBlobUrl]);

  /**
   * Navigates to the local recording view.
   * Cleans up any existing blob URL before starting a new recording.
   */
  const goToLocalRecording = () => {
    cleanupLocalAudioBlobUrl();
    updateCurrentView(VIEWS.LOCAL_RECORDING);
  }

  /**
   * Navigates to the local playback view with the recorded audio.
   * Memoized to prevent unnecessary re-renders that cause the recorder to restart.
   * @param {string} blobUrl - The blob URL of the recorded audio.
   */
  const goToLocalPlayback = useCallback((blobUrl) => {
    setLocalAudioBlobUrl(blobUrl);
    updateCurrentView(VIEWS.LOCAL_PLAYBACK);
  }, []);

  /**
   * Handles when user confirms mic is fixed from local playback view.
   * Navigates to retry view with success state.
   */
  const handleLocalTestFixed = () => {
    cleanupLocalAudioBlobUrl();
    setRetryPatter(asrCopy.teacher_modal_retry_return_1);
    speechProps.speakWithPromise({ textToSpeak: asrCopy.teacher_modal_retry_return_1 });
    goToRetry();
  }

  /**
   * Handles when user wants to retest audio from local playback view.
   * Navigates to the retest microphone view with troubleshooting tips.
   */
  const handleLocalTestRetest = () => {
    cleanupLocalAudioBlobUrl();
    // Navigate to retest microphone view with troubleshooting tips
    updateCurrentView(VIEWS.RETEST_MICROPHONE);
  }

  /**
   * Handles recording errors from LocalRecordingView.
   * Memoized to prevent unnecessary re-renders that cause the recorder to restart.
   */
  const handleLocalRecordingError = useCallback((error) => {
    console.error('Local recording error:', error);
    // On error, go back to test microphone view
    updateCurrentView(VIEWS.TEST_MICROPHONE);
  }, []);

  const renderCurrentView = () => {
    switch (currentView) {
      case VIEWS.RETRY:
        return (
          <RetryView
            speechProps={speechProps}
            retryPatter={retryPatter}
            handleRetry={props.handleRetry}
            studentDidTry={props.studentDidTry}
            handleTroubleshoot={() => {
              speechProps.abortSpeech()
              setRetryPatter(undefined);

              // Show the repeat interstitial if the user has already been through the troubleshoot flow once
              if (midSessionTroubleshootSeen) {
                updateCurrentView(VIEWS.REPEAT_AUDIO_ISSUE_INTERSTITIAL);
                return;
              }

              // Route to audio issue interstitial if audio issue type flags are set
              const hasAudioIssueType = audioIssueType.isClipped || audioIssueType.deviceIssue;

              // Set the mount patter only on first visit
              if (isFirstRetryVisit && !hasAudioIssueType) {
                setLandingPatter(asrCopy.teacher_modal_landing)
              }
              
              if (hasAudioIssueType) {
                goToAudioIssueInterstitial()
              } else {
                goToListenToRecording()
              }
            }}
            isFirstVisit={isFirstRetryVisit}
            midSessionMicTroubleshoot={midSessionMicTroubleshoot}
            onRestartSession={onRestartSession}
          />
        )
      case VIEWS.LANDING:
        return (
          <LandingView
            speechProps={speechProps}
            goToListenToRecording={() => {
              setLandingPatter(null)
              goToListenToRecording()
            }}
            goToCheckHeadset={() => {
              setLandingPatter(null)
              goToCheckHeadset()
            }}
            goToRetry={() => {
              goToRetry()
            }}
            hasVisistedListenToRecording={hasVisistedListenToRecording}
            highlightLandingRetryButton={teacherTestResult === 'backgroundNoise'}
            mountPatter={landingPatter}
            midSessionMicTroubleshoot={midSessionMicTroubleshoot}
            onRestartSession={onRestartSession}
          />
        )
      case VIEWS.LISTEN_TO_RECORDING:
        return (
          <ListenToRecordingView
            goToRetry={goToRetry}
            goToCheckHeadset={goToCheckHeadset}
            goToStudentUseIssue={goToStudentUseIssue}
            speechProps={speechProps}
            mountPatter={landingPatter}
          />
        )
      case VIEWS.CHECK_HEADSET:
        return (
          <CheckHeadsetView
            speechProps={speechProps}
            asrCopy={asrCopy}
            goToListenToRecording={checkHeadsetBackNav.current || goToListenToRecording}
            goToTestMicrophone={goToTestMicrophone}
          />
        )
      case VIEWS.TEST_MICROPHONE:
        return (
          <TestMicrophoneView
            goToCheckHeadset={goToCheckHeadset}
            goToLocalRecording={goToLocalRecording}
            speechProps={speechProps}
          />
        )
      case VIEWS.RUNNING_ASR_TEST:
        return (
          <ASRTestView
            goToLanding={goToLanding}
            speechProps={speechProps}
            onStartPatter={asrCopy.teacher_mode_asr_initial_prompt_two}
            history={props.history}
          />
        )
      case VIEWS.ADDITIONAL_DETAILS:
        return (
          <AdditionalDetailsView
            onClickNo={() => {
              goToRetry()
            }}
            onClickYes={() => {
              goToRetry()
            }}
          />
        )
      case VIEWS.RETRY_AFTER_FIRST_FAILURE:
        return (
          <TeacherRetryView
            goToASRTest={goToASRTest}
          />
        )
      case VIEWS.ERROR:
        return (
          <ErrorView
            speechProps={speechProps}
            asrCopy={asrCopy}
            onExit={() => {
              setRetryPatter(undefined);
              goToRetry()
            }}
          />
        )
      case VIEWS.LOCAL_RECORDING:
        return (
          <LocalRecordingView
            onRecordingComplete={goToLocalPlayback}
            onError={handleLocalRecordingError}
          />
        )
      case VIEWS.LOCAL_PLAYBACK:
        return (
          <LocalPlaybackView
            audioUrl={localAudioBlobUrl}
            onFixed={handleLocalTestFixed}
            onRetest={handleLocalTestRetest}
            onCleanup={cleanupLocalAudioBlobUrl}
          />
        )
      case VIEWS.RETEST_MICROPHONE:
        return (
          <RetestMicrophoneView
            goToLocalRecording={goToLocalRecording}
            speechProps={speechProps}
          />
        )
      case VIEWS.STUDENT_USE_ISSUE:
        return (
          <StudentUseIssueView
            goBack={studentUseIssueBackNav.current || goToListenToRecording}
            onExitTroubleshoot={() => {
              goToRetry()
            }}
          />
        )
      case VIEWS.AUDIO_ISSUE_INTERSTITIAL:
        return (
          <AudioIssueInterstitialView
            audioIssueType={audioIssueType}
            goToStudentUseIssue={goToStudentUseIssue}
            goToCheckHeadset={goToCheckHeadset}
            goToListenToRecording={goToListenToRecording}
            goToRetry={goToRetry}
            goToAudioIssueInterstitial={goToAudioIssueInterstitial}
          />
        )
      case VIEWS.REPEAT_AUDIO_ISSUE_INTERSTITIAL:
        return (
          <RepeatAudioIssueInterstitialView
            onTroubleshoot={() => {
              const hasAudioIssueType = audioIssueType.isClipped || audioIssueType.deviceIssue;
              if (hasAudioIssueType) {
                goToAudioIssueInterstitial()
              } else {
                goToListenToRecording()
              }
            }}
            onAllowContinue={onRestartSessionSuppressingTroubleshoot}
          />
        )
      default:
        return null
    }
  }

  return (
    <div className="asr-teacher-modal">
      <div className="asr-teacher-modal__student-content __student-content">
        <StudentInstruction fullHeight={true}>
          <div className="asr-teacher-modal__container">
            {currentView !== VIEWS.RETRY && currentView !== VIEWS.LOCAL_RECORDING && currentView !== VIEWS.RETEST_MICROPHONE && <Heading />}
            <div className="asr-teacher-modal__content">
              {renderCurrentView()}
            </div>
          </div>
        </StudentInstruction>
      </div>
    </div>
  )
}